import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Dialog,
  Paper,
  Box,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import SearchIcon from "@mui/icons-material/Search";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import AddIcon from "@mui/icons-material/Add";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";

import TileIcon from "../../assets/images/TileIcon.svg";
import TileIconPink from "../../assets/images/TileIconPink.svg";
import {
  TOOL_PROMPT_CATEGORY_GROUPS,
  ALL_TOOL_PROMPT_CATEGORIES,
  TRAINING_COMPASS_TOOL_ID,
  EXTERNAL_SOURCES_TOOL_ID,
} from "../../sampledata/PromptDialogData";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import "./PromptDialog.scss";
import { inputUpdate } from "../../redux/slice/snackbar";
import {
  fetchPromptLibrary,
  fetchFavorites,
  toggleLibraryFavorite,
  addCustomFavoriteThunk,
  removeFavoriteById,
} from "../../redux/slice/promptLibrary";
import {
  injectProductGroups,
  PRODUCT_INJECTION_PERSONAS,
} from "../../utils/promptUtils";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

const CustomTabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>{children}</Box>
      )}
    </div>
  );
};

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

// Debounce hook
function useDebouncedValue(value, delay = 300) {
  const [debounced, setDebounced] = React.useState(value);

  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}

export default function PromptDialog(props) {
  const {
    dialogOpen,
    handleDialogClose,
    userRole = "SALESREP",
    activeToolId = null,
  } = props;

  const [value, setValue] = React.useState(0);
  const [searchFocused, setSearchFocused] = React.useState(false);

  // search state
  const [searchText, setSearchText] = React.useState("");
  const debouncedSearch = useDebouncedValue(searchText, 300);

  // "+ Add new favorite" inline input state
  const [addingFav, setAddingFav] = React.useState(false);
  const [newFavText, setNewFavText] = React.useState("");
  const [addFavError, setAddFavError] = React.useState("");

  const dispatch = useDispatch();
  const repProductGroup = useSelector((state) => state.repEmails.productGroup);
  const libraryCategories = useSelector(
    (state) => state.promptLibrary.categories,
  );
  const favorites = useSelector((state) => state.promptLibrary.favorites);
  const libraryStatus = useSelector(
    (state) => state.promptLibrary.libraryStatus,
  );
  const favoritesStatus = useSelector(
    (state) => state.promptLibrary.favoritesStatus,
  );
  const libraryError = useSelector((state) => state.promptLibrary.error);
  const authUser = useSelector((state) => state.auth.user);

  // Role resolved from the auth slice (populated by GET /api/user-info via
  // loadCurrentUser) — the same source My Performance / ChatContent use, and a
  // DIFFERENT source from the JWT claim the prompt-library backend reads. Used
  // both for client-side product-group injection and as the explicit audience
  // override below. Not the userRole prop, whose "SALESREP" default would
  // misclassify RBD/Leadership if a parent omits it.
  const normalizedRole = (authUser?.business_function || userRole || "")
    .trim()
    .toUpperCase();

  // Favorites tab is hidden for tool-scoped categories. When shown it sits
  // first (index 0), so category tabs/panels are shifted by this offset.
  const showFavourites =
    activeToolId !== TRAINING_COMPASS_TOOL_ID &&
    activeToolId !== EXTERNAL_SOURCES_TOOL_ID;
  const categoryTabOffset = showFavourites ? 1 : 0;

  // Stars are disabled until favorites (and thus the prompt_id -> favorite_id
  // map used for unstarring) have loaded, so we never try to unstar with no id.
  const favsReady = favoritesStatus === "succeeded";

  const handleAddCustomFav = () => {
    const text = newFavText.trim();
    if (!text) return;
    setAddFavError("");
    dispatch(addCustomFavoriteThunk(text))
      .unwrap()
      .then(() => {
        setNewFavText("");
        setAddingFav(false);
      })
      .catch((err) => {
        setAddFavError(err?.message || "Could not add favorite.");
      });
  };

  // Build categories & flattened list for search from the backend response.
  const { categories, promptsByCategory, allPrompts } = React.useMemo(() => {
    const rawCategories = libraryCategories || {};

    // Only RBD / SALESREP get [insert product] replaced; leadership stays as is.
    // injectProductGroups returns new objects, so prompt_id / is_favorite are
    // preserved while only the prompt text changes.
    const injected = PRODUCT_INJECTION_PERSONAS.includes(normalizedRole)
      ? injectProductGroups(rawCategories, repProductGroup)
      : rawCategories;

    const toolCategoryNames = TOOL_PROMPT_CATEGORY_GROUPS[activeToolId];

    const filtered = toolCategoryNames
      ? Object.fromEntries(
          toolCategoryNames
            .filter((cat) => injected[cat])
            .map((cat) => [cat, injected[cat]]),
        )
      : Object.fromEntries(
          Object.entries(injected).filter(
            ([cat]) => !ALL_TOOL_PROMPT_CATEGORIES.includes(cat),
          ),
        );

    const catNames = Object.keys(filtered);

    const flattened = catNames.flatMap((cat) =>
      (filtered[cat] || []).map((row) => ({ category: cat, ...row })),
    );

    // Search pool also includes favorites that aren't catalog rows — notably
    // custom favorites (prompt_id === null), which live only in `favorites`.
    // De-dupe against the catalog by prompt_id so an already-favorited library
    // prompt isn't listed twice. Favorites are out of scope in tool mode.
    let searchPool = flattened;
    if (showFavourites) {
      const catalogIds = new Set(
        flattened.map((r) => r.prompt_id).filter((id) => id != null),
      );
      const favItems = (favorites || [])
        .filter((f) => f.prompt_id == null || !catalogIds.has(f.prompt_id))
        .map((f) => ({
          category: f.category,
          prompt: f.prompt,
          prompt_id: f.prompt_id,
          favorite_id: f.favorite_id,
          is_custom: f.is_custom,
          is_favorite: true,
        }));
      searchPool = [...flattened, ...favItems];
    }

    return {
      categories: catNames,
      promptsByCategory: filtered,
      allPrompts: searchPool,
    };
  }, [
    libraryCategories,
    normalizedRole,
    activeToolId,
    repProductGroup,
    favorites,
    showFavourites,
  ]);

  // Favourites tab list, with the same product-group injection applied.
  const favouritesInjected = React.useMemo(
    () =>
      PRODUCT_INJECTION_PERSONAS.includes(normalizedRole)
        ? injectProductGroups(favorites, repProductGroup)
        : favorites,
    [favorites, normalizedRole, repProductGroup],
  );

  const highlightText = (text, query) => {
    if (!query) return text;

    const safeText = String(text);
    const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const regex = new RegExp(`(${escapedQuery})`, "gi");

    return safeText.replace(
      regex,
      '<mark class="prompt-search-highlight">$1</mark>',
    );
  };

  const handleInputDataFromPrompt = (question) => {
    handleDialogClose();
    dispatch(inputUpdate(String(question)));
  };

  const trimmedQuery = debouncedSearch.trim();
  const isSearching = trimmedQuery.length > 0;

  const filteredResults = React.useMemo(() => {
    if (!isSearching) return [];
    const q = trimmedQuery.toLowerCase();
    return allPrompts.filter((item) =>
      (item.prompt || "").toLowerCase().includes(q),
    );
  }, [isSearching, trimmedQuery, allPrompts]);

  // Fetch library + favorites in parallel each time the dialog opens; reset
  // local UI state on close.
  React.useEffect(() => {
    if (dialogOpen) {
      // TODO: Temporary workaround for backend bug — business_function is
      // missing from the JWT for delegated logins, so GET /prompt-library's
      // server-side audience resolution falls back incorrectly. We pass the
      // audience explicitly from the role resolved via /api/user-info (auth
      // slice), which is correct. Remove this explicit audience override once
      // auth.py embeds business_function at token-issuance time. See [ticket link].
      dispatch(fetchPromptLibrary(normalizedRole));
      dispatch(fetchFavorites());
    } else {
      setSearchText("");
      setValue(0);
      setAddingFav(false);
      setNewFavText("");
      setAddFavError("");
    }
  }, [dialogOpen, dispatch, normalizedRole]);

  const hasLibrary = categories.length > 0;
  // First-load only: show skeletons while fetching with nothing cached yet, so
  // reopening the dialog keeps the previous content instead of flickering.
  const isContentLoading =
    !hasLibrary &&
    (libraryStatus === "idle" ||
      libraryStatus === "loading" ||
      favoritesStatus === "loading");
  const showErrorState = !hasLibrary && libraryStatus === "failed";

  const handleRetry = () => {
    // See the audience-override TODO in the dialog-open effect below.
    dispatch(fetchPromptLibrary(normalizedRole));
    dispatch(fetchFavorites());
  };

  const renderSkeletonTiles = (count = 6) =>
    Array.from({ length: count }).map((_, i) => (
      <Skeleton
        key={i}
        variant="rounded"
        animation="wave"
        className="prompt-library-tabs-panel-paper"
        sx={{ height: 60, bgcolor: "rgba(0,0,0,0.08)" }}
      />
    ));

  // Star toggle on a catalog prompt tile (reflects row.is_favorite).
  const renderFavButton = (row) => (
    <IconButton
      size="small"
      className="prompt-tile-fav"
      disabled={!favsReady}
      aria-label={row.is_favorite ? "Remove favorite" : "Add favorite"}
      onClick={(e) => {
        e.stopPropagation();
        if (!favsReady) return;
        // Custom favorites have no prompt_id — unstar by favorite_id instead.
        if (row.is_custom || row.prompt_id == null) {
          if (row.favorite_id) dispatch(removeFavoriteById(row.favorite_id));
        } else {
          dispatch(toggleLibraryFavorite(row));
        }
      }}
      sx={{
        p: 0.5,
        color: row.is_favorite ? "#E56800" : "#9E9E9E",
        opacity: favsReady ? 1 : 0.4,
      }}
    >
      {row.is_favorite ? (
        <StarIcon sx={{ fontSize: 18 }} />
      ) : (
        <StarBorderIcon sx={{ fontSize: 18 }} />
      )}
    </IconButton>
  );

  return (
    <BootstrapDialog
      className="prompt-dialog-parent"
      onClose={handleDialogClose}
      aria-labelledby="customized-dialog-title"
      open={dialogOpen}
      maxWidth="md"
      fullWidth
      InputProps={{
        sx: { maxWidth: "820px" },
        className: "prompt-dialog-parent-input-props",
      }}
    >
      <DialogTitle
        sx={{ m: 0, p: 2, fontSize: "16px", fontWeight: "bold" }}
        id="customized-dialog-title"
      >
        Prompt Library
      </DialogTitle>

      <Typography
        sx={{ mx: 2, fontSize: "12px", fontWeight: "400", color: "#6F6F6F" }}
      >
        Explore prompts to create, edit and get more done.
      </Typography>

      <IconButton
        aria-label="close"
        onClick={handleDialogClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ px: 2, pt: 2 }} className="prompt-library-search">
        <TextField
          fullWidth
          size="small"
          placeholder="Search keyword.."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className="prompt-library-search-input"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{
                    fontSize: 18,
                    color: searchFocused ? "#E56800" : "#9E9E9E",
                  }}
                />
              </InputAdornment>
            ),
            endAdornment: searchText && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchText("")}
                  sx={{
                    color: searchFocused ? "#E56800" : "#9E9E9E",
                    "&:hover": { background: "transparent" },
                  }}
                >
                  <CancelOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {isSearching && (
          <Typography
            sx={{
              mt: 1,
              fontSize: "12px",
              color: "#6F6F6F",
            }}
          >
            {filteredResults.length} result
            {filteredResults.length === 1 ? "" : "s"}
          </Typography>
        )}
      </Box>

      {/* TABS (hidden when searching or only one tab) */}
      {!isSearching && categories.length + (showFavourites ? 1 : 0) > 1 && (
        <Box className="prompt-dialog-box">
          <Tabs
            value={value}
            onChange={(_, v) => setValue(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            className="propmt-library-tabs"
            sx={{ mt: 3, ml: 2 }}
          >
            {showFavourites && <Tab key="__favourites__" label="Favorites" />}
            {categories.map((cat) => (
              <Tab key={cat} label={cat} />
            ))}
          </Tabs>
        </Box>
      )}

      <DialogContent className="prompt-dialog-content">
        <Box sx={{ width: "100%" }} className="prompt-library-tabs-content">
          {/* Initial load: skeleton tiles (graceful 400 / 503 → error + retry) */}
          {isContentLoading ? (
            <Box
              sx={{ display: "flex", flexWrap: "wrap" }}
              className="prompt-library-tabs-panel"
            >
              {renderSkeletonTiles()}
            </Box>
          ) : showErrorState ? (
            <Box sx={{ width: "100%", px: 1, py: 3, textAlign: "center" }}>
              <Typography sx={{ fontSize: "14px", color: "#6F6F6F", mb: 1.5 }}>
                {libraryError?.message ||
                  "Prompts are unavailable right now. Please try again later."}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleRetry}
                sx={{
                  textTransform: "none",
                  borderColor: "#E56800",
                  color: "#E56800",
                  "&:hover": { borderColor: "#CC5D00" },
                }}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <>
              {/* SEARCH RESULTS VIEW */}
              {isSearching ? (
                <Box
                  sx={{ display: "flex", flexWrap: "wrap" }}
                  className="prompt-library-tabs-panel"
                >
                  {filteredResults.map((item, idx) => (
                    <Paper
                      className="prompt-library-tabs-panel-paper"
                      variant="outlined"
                      key={
                        item.prompt_id ??
                        item.favorite_id ??
                        `${item.category}-${idx}`
                      }
                      sx={{ background: "#F3F3F3" }}
                      onClick={() => handleInputDataFromPrompt(item.prompt)}
                    >
                      <img
                        src={TileIcon}
                        alt="TileIcon"
                        className="prompt-tile-icon"
                      />
                      <img
                        src={TileIconPink}
                        alt="TileIconPink"
                        className="prompt-tile-icon-pink"
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            wordWrap: "break-word",
                            overflowWrap: "break-word",
                            whiteSpace: "normal",
                          }}
                          dangerouslySetInnerHTML={{
                            __html: highlightText(item.prompt, trimmedQuery),
                          }}
                        />
                      </Box>
                      {showFavourites && renderFavButton(item)}
                    </Paper>
                  ))}

                  {filteredResults.length === 0 && (
                    <Box sx={{ px: 1, py: 2 }}>
                      <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        No prompts found. Try a different keyword.
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                /* NORMAL TABS VIEW */
                categories.map((cat, idx) => (
                  <CustomTabPanel
                    key={cat}
                    value={value}
                    index={idx + categoryTabOffset}
                    className="prompt-library-tabs-panel"
                  >
                    {(promptsByCategory[cat] || []).map((row) => (
                      <Paper
                        className="prompt-library-tabs-panel-paper"
                        variant="outlined"
                        key={row.prompt_id}
                        sx={{ background: "#F3F3F3" }}
                        onClick={() => handleInputDataFromPrompt(row.prompt)}
                      >
                        <img
                          src={TileIcon}
                          alt="TileIcon"
                          className="prompt-tile-icon"
                        />
                        <img
                          src={TileIconPink}
                          alt="TileIconPink"
                          className="prompt-tile-icon-pink"
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {row.prompt}
                          </Typography>
                        </Box>
                        {showFavourites && renderFavButton(row)}
                      </Paper>
                    ))}
                  </CustomTabPanel>
                ))
              )}

              {/* FAVORITES PANEL (first tab, index 0) */}
              {!isSearching && showFavourites && (
                <CustomTabPanel
                  value={value}
                  index={0}
                  className="prompt-library-tabs-panel"
                >
                  <Box sx={{ width: "100%", mb: 1 }}>
                    {addingFav ? (
                      <Box>
                        <Box
                          sx={{ display: "flex", gap: 1, alignItems: "center" }}
                        >
                          <TextField
                            fullWidth
                            size="small"
                            autoFocus
                            placeholder="Type a favorite prompt.."
                            value={newFavText}
                            error={Boolean(addFavError)}
                            onChange={(e) => {
                              setNewFavText(e.target.value);
                              if (addFavError) setAddFavError("");
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleAddCustomFav();
                            }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleAddCustomFav}
                            disabled={!newFavText.trim()}
                            sx={{
                              textTransform: "none",
                              background: "#E56800",
                              "&:hover": { background: "#CC5D00" },
                            }}
                          >
                            Save
                          </Button>
                          <Button
                            size="small"
                            onClick={() => {
                              setAddingFav(false);
                              setNewFavText("");
                              setAddFavError("");
                            }}
                            sx={{ textTransform: "none", color: "#6F6F6F" }}
                          >
                            Cancel
                          </Button>
                        </Box>
                        {addFavError && (
                          <Typography
                            sx={{ mt: 0.5, fontSize: "12px", color: "#D32F2F" }}
                          >
                            {addFavError}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                        onClick={() => setAddingFav(true)}
                        sx={{ textTransform: "none", color: "#E56800" }}
                      >
                        Add new favorite
                      </Button>
                    )}
                  </Box>

                  {favouritesInjected.length === 0 ? (
                    <Box sx={{ width: "100%", px: 1, py: 2 }}>
                      <Typography sx={{ fontSize: "14px", color: "#6F6F6F" }}>
                        No favorites yet — star a prompt or add one here.
                      </Typography>
                    </Box>
                  ) : (
                    favouritesInjected.map((fav) => (
                      <Paper
                        className="prompt-library-tabs-panel-paper"
                        variant="outlined"
                        key={fav.favorite_id}
                        sx={{ background: "#F3F3F3" }}
                        onClick={() => handleInputDataFromPrompt(fav.prompt)}
                      >
                        <img
                          src={TileIcon}
                          alt="TileIcon"
                          className="prompt-tile-icon"
                        />
                        <img
                          src={TileIconPink}
                          alt="TileIconPink"
                          className="prompt-tile-icon-pink"
                        />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: "14px",
                              wordWrap: "break-word",
                              overflowWrap: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {fav.prompt}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          className="prompt-tile-fav"
                          aria-label="Remove favorite"
                          onClick={(e) => {
                            e.stopPropagation();
                            dispatch(removeFavoriteById(fav.favorite_id));
                          }}
                          sx={{ p: 0.5, color: "#E56800" }}
                        >
                          <StarIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Paper>
                    ))
                  )}
                </CustomTabPanel>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </BootstrapDialog>
  );
}

PromptDialog.propTypes = {
  dialogOpen: PropTypes.bool.isRequired,
  handleDialogClose: PropTypes.func.isRequired,
  userRole: PropTypes.string, // "SALESREP" | "RBD" | "LEADERSHIP"
  activeToolId: PropTypes.string,
};
